import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiClient, setAccessToken, startTokenKeepAlive, stopTokenKeepAlive, setAuthRequiredHandler, clearTokens } from '@/lib/api-client';

interface User {
  id: string | number;
  username: string;
  email?: string;
  role?: string;
  // 根据你后端的实际返回字段补充
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // 统一注册"登录态彻底失效"出口：当 Refresh Token 也被服务端拒绝时，
  // api-client 的 fetchWithAuth / RestClient 会调用此回调，跳登录页并清空本地态。
  useEffect(() => {
    setAuthRequiredHandler(() => {
      clearTokens();
      setUser(null);
      stopTokenKeepAlive();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    });
    return () => setAuthRequiredHandler(null);
  }, []);

  // 初始化：应用加载时检查用户是否已登录（结合 api-client 的无感刷新机制）
  useEffect(() => {
    const initAuth = async () => {
      try {
        const res = await apiClient.get('/auth/me') as any;
        if (res.data) {
          setUser(res.data);
          startTokenKeepAlive(); // 已登录态：启动主动续期保活
        }
      } catch {
        // 未登录或会话过期，保持在当前页面（登录页或公开页）
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    initAuth();
  }, []);

  const login = async (username: string, password: string) => {
    // 💡 注意：如果你使用的是 Python FastAPI，默认标准的 OAuth2PasswordRequestForm 需要 form-data
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);

    // 如果你的后端接收 JSON，可直接改成 apiClient.post('/auth/login', { username, password })
    const res = await apiClient.post('/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    }) as any;

    const { access_token } = res.data;

    // 1. 将 Token 注入到全局内存中，供后续请求使用
    setAccessToken(access_token);

    // 2. 登录成功后，立即拉取当前用户信息
    const userRes = await apiClient.get('/auth/me') as any;
    setUser(userRes.data);
    startTokenKeepAlive(); // 启动主动续期保活
  };

  const logout = async () => {
    // 可选：通知后端注销（清除服务端的 Refresh Token Cookie）
    try { await apiClient.post('/auth/logout'); } catch { /* ignore logout error */ }

    setAccessToken(null);
    setUser(null);
    stopTokenKeepAlive(); // 停止保活
    // 登出时同样使用硬跳转，确保内存和前端缓存中的敏感数据彻底清空
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth 必须在 AuthProvider 内部使用');
  }
  return context;
}
